import type { LocalUserChoices } from '@livekit/components-core'

export const INTERVIEW_LEGAL_DOCUMENTS = {
  userAgreement: {
    key: 'user_agreement',
    title: 'Пользовательское соглашение',
    version: '1.1',
    url: '/documents/HURMA_Terms_of_Service_v_1_1.pdf',
  },
  personalDataPolicy: {
    key: 'personal_data_policy',
    title: 'Политика обработки персональных данных',
    version: '1.1',
    url: '/documents/Personal_Data_Policy_v_1_1.pdf',
  },
  personalDataConsent: {
    key: 'personal_data_consent',
    title: 'Согласие на обработку персональных данных',
    version: '1.2',
    url: '/documents/Consent_Candidate_PD_v_1_2.pdf',
  },
  videoRecordingConsent: {
    key: 'video_recording_consent',
    title: 'Согласие на видеозапись интервью',
    version: '1.1',
    url: '/documents/Consent_Interview_Recording_v_1_1.pdf',
  },
} as const

export interface InterviewConsentSnapshot {
  personalData: {
    accepted: boolean
    acceptedAt: string | null
    document: typeof INTERVIEW_LEGAL_DOCUMENTS.personalDataConsent
  }
  videoRecording: {
    accepted: boolean
    acceptedAt: string | null
    document: typeof INTERVIEW_LEGAL_DOCUMENTS.videoRecordingConsent
  }
}

export type LocalUserChoicesWithConsents = LocalUserChoices & {
  phone?: string
  interviewConsents?: InterviewConsentSnapshot
}

const CONSENT_SESSION_STORAGE_PREFIX = 'interview-consent-session:'

function getOrCreateCandidateSessionId(roomName: string): string | undefined {
  if (typeof window === 'undefined') return undefined

  const storageKey = `${CONSENT_SESSION_STORAGE_PREFIX}${roomName}`

  try {
    const saved = window.localStorage.getItem(storageKey)
    if (saved) return saved

    const nextId =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    window.localStorage.setItem(storageKey, nextId)
    return nextId
  } catch {
    return undefined
  }
}

export function buildInterviewConsentSnapshot(
  personalDataAcceptedAt: string | null,
  videoRecordingAcceptedAt: string | null,
): InterviewConsentSnapshot {
  return {
    personalData: {
      accepted: Boolean(personalDataAcceptedAt),
      acceptedAt: personalDataAcceptedAt,
      document: INTERVIEW_LEGAL_DOCUMENTS.personalDataConsent,
    },
    videoRecording: {
      accepted: Boolean(videoRecordingAcceptedAt),
      acceptedAt: videoRecordingAcceptedAt,
      document: INTERVIEW_LEGAL_DOCUMENTS.videoRecordingConsent,
    },
  }
}

export async function recordInterviewConsentJournal(input: {
  roomName: string
  values: LocalUserChoices
  phone?: string | null
  referrer?: string | null
  project?: string | string[] | null
  interviewId?: string | null
}) {
  const values = input.values as LocalUserChoicesWithConsents
  if (!values.interviewConsents) return

  const submittedPhone = String(values.phone || input.phone || '').trim()
  const response = await fetch('/api/consent_journal', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      roomName: input.roomName,
      candidateSessionId: getOrCreateCandidateSessionId(input.roomName),
      candidateName: values.username || '',
      phone: submittedPhone || null,
      referrer: input.referrer || null,
      project: typeof input.project === 'string' ? input.project : null,
      interviewId: input.interviewId || null,
      roomJoinClickedAt: new Date().toISOString(),
      consents: values.interviewConsents,
    }),
  })

  let payload: { ok?: boolean; reason?: string } = {}
  try {
    payload = await response.json()
  } catch {
    payload = {}
  }

  if (!response.ok || payload.ok !== true) {
    throw new Error(payload.reason || 'consent_journal_failed')
  }
}
